/**
 * Конфигурация приложения.
 *
 * Значения берутся из `/config.js`, который в контейнере генерируется на старте
 * из переменных окружения. Vite вшивает `import.meta.env` на этапе сборки, а
 * один и тот же образ едет на dev, preprod и prod — поэтому адрес API не может
 * быть частью сборки. Переменные `VITE_*` остаются запасным вариантом для
 * локального запуска без `/config.js`.
 */

interface AppConfig {
  /**
   * Корневой адрес бэкенда. По умолчанию «/api» — тот же origin с тем же
   * префиксом, что и на сервере: локально его срезает прокси Vite, на
   * сервере — Traefik.
   */
  readonly apiBaseUrl: string;
  /** Имя окружения: local, dev, preprod, prod. */
  readonly environment: string;
}

/**
 * Первое непустое значение.
 *
 * Именно непустое, а не первое не-undefined: незаполненная переменная в .env
 * приходит пустой строкой, и это означает «не задано», а не «пустой адрес».
 * Оператор `??` такую разницу выразить не может.
 */
function firstFilled(...values: (string | undefined)[]): string | undefined {
  return values.find((value) => value !== undefined && value !== '');
}

function readConfig(): AppConfig {
  const runtime = typeof window === 'undefined' ? undefined : window.__FLOWDO_CONFIG__;

  return {
    apiBaseUrl: firstFilled(runtime?.apiBaseUrl, import.meta.env.VITE_API_BASE_URL) ?? '/api',
    environment: firstFilled(runtime?.environment, import.meta.env.VITE_ENVIRONMENT) ?? 'unknown',
  };
}

export const appConfig: AppConfig = readConfig();
